<script>
import { defineComponent, ref, watch, computed, inject } from 'vue'
import { useQuestionKeyboardNav } from '@/composables/useQuestionKeyboardNav'

export default defineComponent({
  name: 'GuidedQuestion',
  props: {
    question: {
      type: Object,
      required: true
    },
    currentIndex: {
      type: Number,
      required: true
    },
    totalQuestions: {
      type: Number,
      required: true
    },
    previousAnswer: {
      type: Object,
      default: null
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    canDelete: {
      type: Boolean,
      default: false
    }
  },
  emits: ['answer', 'back', 'restart', 'delete'],
  setup(props, { emit }) {
    const loginState = inject('loginState');
    const selectedKey = ref(props.previousAnswer?.selectedKey ?? null)
    const comments = ref(props.previousAnswer?.comments ?? null)
    const editComment = ref(false)
    const updatedBy = computed(() => props.previousAnswer?.updated_by ?? loginState.user.name)
    const updatedDate = computed(() => props.previousAnswer && props.previousAnswer.comments ? props.previousAnswer.updated_date : null)

    // An empty rationale is stored as null, so normalise before comparing —
    // otherwise opening and closing the editor would look like a change.
    function normaliseComment(value) {
      const text = (value ?? '').trim()
      return text === '' ? null : text
    }

    // True only when the selected option or the rationale differs from the stored
    // answer, so Next can skip a pointless save.
    const isDirty = computed(() =>
      selectedKey.value !== (props.previousAnswer?.selectedKey ?? null) ||
      normaliseComment(comments.value) !== normaliseComment(props.previousAnswer?.comments)
    )

    watch(() => props.question.id, () => {
    }, { immediate: true })

    watch(() => props.previousAnswer, (newVal) => {
      selectedKey.value = newVal?.selectedKey ?? null
      comments.value = newVal?.comments ?? null
      editComment.value = false
    })

    function selectOption(key) {
      selectedKey.value = key
    }

    function submitAnswer() {
      if (selectedKey.value === null) return

      const option = props.question.answerOptions.find(o => o.key === selectedKey.value)
      if (!option) return

      emit('answer', {
        questionId: props.question.id,
        selectedKey: selectedKey.value,
        selectedText: option.text,
        comments: comments.value,
        dirty: isDirty.value
      })
    }

    function cancelComment() {
      comments.value = props.previousAnswer?.comments ?? null
      editComment.value = false
    }

    function submitComment() {
      editComment.value = false
    }

    const { keyHint } = useQuestionKeyboardNav({
      options: () => (props.question.answerOptions || []).map((o, i) => ({ key: i + 1, value: o.key })),
      onSelect: selectOption,
      onNext: submitAnswer,
      onPrev: () => emit('back'),
      canNext: () => selectedKey.value !== null,
      verb: 'select',
    })

    return {
      selectedKey,
      selectOption,
      submitAnswer,
      isDirty,
      keyHint,
      comments,
      editComment,
      updatedBy,
      updatedDate,
      cancelComment,
      submitComment,
    }
  }
})
</script>

<template>
  <div class="lvl-eval">
    <div class="lvl-qcard">
      <div class="lvl-qcard-in">
        <!-- Question label -->
        <div class="lvl-q-eyebrow">Question {{ question.id }}</div>

        <!-- Question text -->
        <h2 class="lvl-q-title">{{ question.question }}</h2>

        <!-- Sub-factor info (if available) -->
        <p v-if="question.subFactorTitle" class="lvl-q-sub">
          Related to: {{ question.subFactorTitle }}
        </p>

        <!-- Selection prompt -->
        <div class="lvl-q-prompt">Select your answer:</div>

        <!-- Answer options -->
        <div class="lvl-trk-opts" role="radiogroup">
          <button
            v-for="option in question.answerOptions"
            :key="option.key"
            type="button"
            role="radio"
            :aria-checked="selectedKey === option.key"
            class="lvl-trk-opt"
            :class="{ on: selectedKey === option.key }"
            @click="selectOption(option.key)"
            :data-testid="'guided-question-' + question.id + '-option-' + option.key"
          >
            <span class="lvl-trk-radio" :class="{ on: selectedKey === option.key }"></span>
            <span class="lvl-trk-main">
              <span class="lvl-trk-name"><span class="lvl-trk-lvln">{{ option.key }}</span>{{ option.text }}</span>
            </span>
          </button>
        </div>

        <!-- Rationale -->
        <div v-if="editComment" class="lvl-q-rationale">
          <label for="evaluation-rationale">Rationale</label>
          <textarea
            id="evaluation-rationale"
            class="lvl-textarea"
            v-model="comments"
            placeholder="Explain your reasoning or add context..."
          ></textarea>
          <div class="lvl-q-rationale-btns">
            <button type="button" class="lvl-btn lvl-btn--outlined" @click="cancelComment">Cancel</button>
            <button type="button" class="lvl-btn" @click="submitComment">Add rationale</button>
          </div>
        </div>
        <div v-else-if="comments" class="lvl-q-rationale-view">
          <h5>
            <span>{{ updatedBy }}</span>
            <button type="button" @click="editComment = true"><span class="ms" style="font-size:18px">edit</span></button>
          </h5>
          <div class="rtxt">{{ comments }}</div>
          <span v-if="updatedDate" class="lvl-q-rationale-time">{{ updatedDate }}</span>
        </div>
        <div v-else class="lvl-q-rationale-add">
          <button type="button" @click="editComment = true"><span class="ms" style="font-size:18px">edit</span> Add rationale</button>
          <span class="opt">(optional)</span>
        </div>
      </div>

      <!-- Footer -->
      <div class="lvl-cardfoot">
        <span class="restart">
          <button type="button" class="lvl-btn lvl-btn--outlined" :disabled="readOnly" @click="$emit('restart')">Restart</button>
          <button v-if="canDelete" type="button" class="lvl-btn lvl-btn--danger" :disabled="readOnly" @click="$emit('delete')">Delete</button>
        </span>
        <span class="prog">guided questions {{ currentIndex + 1 }} of {{ totalQuestions }}<template v-if="keyHint"> · {{ keyHint }}</template></span>
        <span class="nav">
          <button type="button" class="lvl-btn lvl-btn--outlined" @click="$emit('back')">Previous</button>
          <button type="button" class="lvl-btn" :disabled="selectedKey === null" @click="submitAnswer">Next <span class="ms">arrow_forward</span></button>
        </span>
      </div>
    </div>
  </div>
</template>

<style lang="scss" src="./evaluationTheme.scss"></style>

<style scoped>
.lvl-q-rationale-time { margin-top: 6px; font-size: 11px; color: var(--v3-text-3); }
</style>
